import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, asList } from '../lib/api';
import { currentAcademicYear } from '../lib/academicYear';

/**
 * ═══ ONE CHARGE, SOME OF THE CHILDREN ═══
 *
 * A cultural fee, an excursion, an exam fee for the ones sitting it, a uniform charge for the eleven
 * who ordered one. The shape a school actually asks for is not "one student" and not "the whole
 * class" — it is the thirty-one who are going.
 *
 * ⚠ THE PREVIEW IS THE SCREEN, exactly as it is for the student import. Nobody presses a button that
 * bills four hundred families without being shown the list — and the half that matters is the
 * children who ALREADY have this charge, because that is the mistake that generates a phone call.
 *
 * ⚠ NOTHING IS WRITTEN UNTIL THE SECOND BUTTON. The preview is a separate request that cannot write,
 * and the commit re-plans server-side rather than trusting the list this screen was shown.
 */

interface Row { studentId: string; admissionNumber: string; fullName: string; alreadyHas: boolean }
interface Preview {
  willRaise: Row[]; alreadyHave: Row[];
  newCount: number; skippedCount: number; totalPaise: number; problem: string | null;
}
interface Result { raised: number; skipped: number; totalPaise: number }
interface Group { id: string; label: string; people: number }
interface StudentRow { id: string; fullName: string; admissionNumber: string; classLabel: string | null }

const rupees = (paise: number) =>
  `₹${(paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

export function AdHocFeeDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  // ⚠ The dialog finds these itself rather than making the page thread them through. A school has
  // one institution in almost every case, and asking the office to pick it before they can raise a
  // cultural fee is a step invented by the data model rather than by the job.
  const [institutionId, setInstitutionId] = useState('');
  const academicYear = currentAcademicYear();
  const [feeTypes, setFeeTypes] = useState<string[]>([]);
  const [classes, setClasses] = useState<Group[]>([]);
  const [classesFailed, setClassesFailed] = useState(false);
  const [feeType, setFeeType] = useState('Activity');
  const [label, setLabel] = useState('');
  const [rupeeAmount, setRupeeAmount] = useState('');
  const [dueDate, setDueDate] = useState('');

  const [who, setWho] = useState<'class' | 'school' | 'pick'>('class');
  const [classId, setClassId] = useState('');
  const [search, setSearch] = useState('');
  const [found, setFound] = useState<StudentRow[]>([]);
  const [picked, setPicked] = useState<StudentRow[]>([]);

  const [preview, setPreview] = useState<Preview | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [busy, setBusy] = useState<'' | 'checking' | 'raising'>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<Array<{ id: string }>>('/api/institutions')
      .then((xs) => setInstitutionId(xs[0]?.id ?? '')).catch(() => undefined);
    api.get<string[]>('/api/fees/fee-types').then((r) => setFeeTypes(asList(r))).catch(() => setFeeTypes([]));
    // ⚠ /api/fees/classes, NOT /api/exports/register/groups. The export endpoint gives the whole
    // school only to TrustAdmin, CampusAdmin, Principal and HrManager and otherwise returns the
    // classes the caller TEACHES — and an Accountant teaches none, so the person whose job this is
    // got an empty dropdown. The fee endpoint sits on the same "finance" policy as this dialog, so
    // whoever may raise a class-wide fee is exactly whoever is offered the classes.
    api.get<Group[]>('/api/fees/classes')
      // ⚠ asList inside the transform. The codemod rewrote every bare `.then(setX)`; this one wraps
      // the setter in a lambda so it was invisible to that pass — and `{}` set straight into a list
      // state is the same crash whether or not a pattern matched it.
      .then((xs) => { setClasses(asList(xs)); setClassesFailed(false); })
      // ⚠ AND A FAILURE IS NO LONGER SILENT. setClasses([]) turned "the call failed" into "this
      // school has no classes", which are indistinguishable in a <select> and led somebody to
      // conclude the feature was broken rather than that they could not see it.
      .catch(() => { setClasses([]); setClassesFailed(true); });
  }, []);

  // ⚠ Any change to the charge or the recipients throws the preview away. A report from the
  // PREVIOUS amount sitting under a newly typed one is how somebody raises a charge they never
  // checked — the same rule as the import screen.
  const invalidate = useCallback(() => { setPreview(null); setResult(null); setError(null); }, []);

  const body = () => ({
    studentIds: who === 'pick' ? picked.map((p) => p.id) : [],
    academicClassId: who === 'class' && classId ? classId : null,
    // ⚠ THE THIRD SCOPE, AND IT GOES THROUGH THE SAME PREVIEW AS THE OTHER TWO. The bigger the
    // blast radius the more the duplicate check matters: charging fourteen hundred children twice
    // is not worse than charging sixty twice by degree, it is unrecoverable.
    allInInstitution: who === 'school',
    institutionId,
    academicYear,
    feeType,
    feeTypeLabel: label.trim() || null,
    // ⚠ RUPEES IN THE BOX, PAISE ON THE WIRE. The office thinks in rupees; the ledger is integer
    // paise everywhere, and a float rupee amount is how ₹1,200.00 becomes ₹1,199.99 in a total.
    amountPaise: Math.round(Number(rupeeAmount || '0') * 100),
    lineLabel: label.trim() || null,
    dueDate: dueDate ? new Date(dueDate).toISOString() : null,
  });

  const check = async () => {
    setBusy('checking'); setError(null); setResult(null);
    try { setPreview(await api.post<Preview>('/api/fees/adhoc/preview', body())); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not work that out.'); }
    finally { setBusy(''); }
  };

  const raise = async () => {
    setBusy('raising'); setError(null);
    try { setResult(await api.post<Result>('/api/fees/adhoc/many', body())); setPreview(null); onDone(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not raise the charge.'); }
    finally { setBusy(''); }
  };

  const doSearch = async (q: string) => {
    setSearch(q);
    if (q.trim().length < 2) { setFound([]); return; }
    try {
      const page = await api.get<{ items: StudentRow[] }>(
        `/api/students?take=20&search=${encodeURIComponent(q.trim())}`);
      setFound(page.items);
    } catch { setFound([]); }
  };

  const toggle = (s: StudentRow) => {
    invalidate();
    setPicked((ps) => ps.some((p) => p.id === s.id) ? ps.filter((p) => p.id !== s.id) : [...ps, s]);
  };

  // ⚠ "Every student" needs nothing chosen, so it counts as ready. Reusing the two-way test here
  //   left the button dead with no explanation — the scope was selectable and did nothing.
  const recipients = who === 'school' ? 1
    : who === 'class' ? (classId ? 1 : 0)
    : picked.length;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 60,
      display: 'grid', placeItems: 'center', padding: 20,
    }}>
      <div onClick={(e) => e.stopPropagation()} className="card"
        style={{ width: 620, maxWidth: '100%', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>

        <div style={{ fontWeight: 650, fontSize: 16, marginBottom: 2 }}>Raise a one-off fee</div>
        <p style={{ fontSize: 12.5, color: 'var(--ink-3)', margin: '0 0 16px', maxWidth: '58ch' }}>
          A cultural fee, an excursion, an exam fee — for a whole class or just the children it
          applies to. It becomes an ordinary invoice, so it shows in the parent's list and can be
          paid online like any other.
        </p>

        {error && (
          <div className="pill pill-danger"
            style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>
            {error}
          </div>
        )}

        {/* ── what the charge is ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
          <div>
            <label className="label">Name on the invoice</label>
            <input className="input" value={label} placeholder="Cultural programme 2026"
              style={{ width: '100%', marginBottom: 12 }}
              onChange={(e) => { setLabel(e.target.value); invalidate(); }} />
          </div>
          <div>
            <label className="label">Amount (₹)</label>
            <input className="input" value={rupeeAmount} inputMode="decimal" placeholder="1200"
              style={{ width: '100%', marginBottom: 12 }}
              onChange={(e) => { setRupeeAmount(e.target.value); invalidate(); }} />
          </div>
          <div>
            <label className="label">Kind</label>
            <select className="input" value={feeType} style={{ width: '100%', marginBottom: 12 }}
              onChange={(e) => { setFeeType(e.target.value); invalidate(); }}>
              {feeTypes.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Due date</label>
            <input className="input" type="date" value={dueDate} style={{ width: '100%', marginBottom: 12 }}
              onChange={(e) => { setDueDate(e.target.value); invalidate(); }} />
          </div>
        </div>

        {/* ── who it applies to ── */}
        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 14, marginBottom: 10 }}>
          <div className="eyebrow" style={{ marginBottom: 8 }}>Who it applies to</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button className="btn" onClick={() => { setWho('class'); invalidate(); }}
              style={{ background: who === 'class' ? 'var(--brand)' : undefined,
                       color: who === 'class' ? '#fff' : undefined }}>
              A whole class
            </button>
            <button className="btn" onClick={() => { setWho('school'); invalidate(); }}
              style={{ background: who === 'school' ? 'var(--brand)' : undefined,
                       color: who === 'school' ? '#fff' : undefined }}>
              Every student
            </button>
            <button className="btn" onClick={() => { setWho('pick'); invalidate(); }}
              style={{ background: who === 'pick' ? 'var(--brand)' : undefined,
                       color: who === 'pick' ? '#fff' : undefined }}>
              Choose students
            </button>
          </div>

          {who === 'school' ? (
            /* ⚠ NOTHING TO CHOOSE, AND IT SAYS SO RATHER THAN SHOWING AN EMPTY BOX. The next thing
               the office does is press Check, and the preview names anybody who already has the
               charge before a single invoice is written. */
            <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: 0 }}>
              Every student on the roll{classes.length > 0 && <> — {classes.reduce((n, c) => n + c.people, 0)} across {classes.length} classes</>}.
              Check it first: the preview lists anybody who already has this charge.
            </p>
          ) : who === 'class' ? (
            <>
            {/* ⚠ AN EMPTY PICKER HAS TO SAY WHY. Three different situations produced the same blank
                dropdown — the call failed, the caller may not see classes, or the school genuinely
                has none — and a person looking at it can only conclude the software is broken. */}
            {(classesFailed || classes.length === 0) && (
              <p data-testid="no-classes" style={{
                margin: '0 0 10px', fontSize: 13.5, lineHeight: 1.5, color: 'var(--ink-2)',
                border: '1px solid var(--line)', borderRadius: 8, padding: '10px 12px',
              }}>
                {classesFailed
                  ? 'The class list could not be loaded. If this keeps happening, your account may not have permission to see classes — ask an administrator.'
                  : 'There are no classes set up yet. Add them under Academics, or raise this fee for chosen students instead.'}
              </p>
            )}
            <select className="input" value={classId} style={{ width: '100%' }}
              disabled={classes.length === 0}
              onChange={(e) => { setClassId(e.target.value); invalidate(); }}>
              <option value="">Choose a class</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.label} · {c.people}</option>)}
            </select>
            </>
          ) : (
            <>
              <input className="input" value={search} placeholder="Search by name or admission number…"
                style={{ width: '100%', marginBottom: 8 }}
                onChange={(e) => void doSearch(e.target.value)} />

              {found.length > 0 && (
                <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid var(--line)',
                              borderRadius: 8, marginBottom: 8 }}>
                  {found.map((s) => (
                    <button key={s.id} onClick={() => toggle(s)}
                      style={{ display: 'flex', gap: 8, width: '100%', textAlign: 'left', font: 'inherit',
                               background: 'none', border: 0, borderBottom: '1px solid var(--line-2)',
                               padding: '7px 10px', cursor: 'pointer', fontSize: 13 }}>
                      <span style={{ width: 16 }}>{picked.some((p) => p.id === s.id) ? '✓' : ''}</span>
                      <span style={{ fontWeight: 600 }}>{s.fullName}</span>
                      <span style={{ color: 'var(--ink-3)' }}>{s.admissionNumber}</span>
                      <span style={{ marginLeft: 'auto', color: 'var(--ink-3)' }}>{s.classLabel ?? ''}</span>
                    </button>
                  ))}
                </div>
              )}

              {picked.length > 0 && (
                <div style={{ fontSize: 12.5, color: 'var(--ink-2)' }}>
                  {picked.length} chosen ·{' '}
                  <button className="btn btn-ghost" style={{ padding: '1px 6px', fontSize: 12 }}
                    onClick={() => { setPicked([]); invalidate(); }}>clear</button>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── the preview ── */}
        {preview && !preview.problem && (
          <div className="card" style={{ padding: 14, margin: '12px 0', background: 'var(--bg-2, #f7f8fa)' }}>
            <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginBottom: 10 }}>
              <Stat n={String(preview.newCount)} label="will be charged" />
              <Stat n={rupees(preview.totalPaise)} label="raised in total" />
              {preview.skippedCount > 0 && (
                <Stat n={String(preview.skippedCount)} label="already have it" warn />
              )}
            </div>

            {/* ⚠ THE HALF THAT MATTERS. Naming the children who already have this charge is what
                stops the second attempt on a Tuesday becoming a double bill — and what lets the
                office see that Tuesday's attempt did work after all. */}
            {preview.alreadyHave.length > 0 && (
              <>
                <div style={{ fontSize: 12.5, fontWeight: 650, color: 'var(--warn)', marginBottom: 4 }}>
                  Already have "{label.trim()}" this year — they will be skipped
                </div>
                <div style={{ fontSize: 13, maxHeight: 110, overflowY: 'auto', marginBottom: 8 }}>
                  {preview.alreadyHave.map((r) => (
                    <div key={r.studentId} style={{ padding: '3px 0' }}>
                      {r.fullName} <span style={{ color: 'var(--ink-3)' }}>{r.admissionNumber}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            {preview.newCount === 0 && (
              <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: 0 }}>
                Everybody selected already has this charge. Nothing will be raised.
              </p>
            )}
          </div>
        )}

        {preview?.problem && (
          <p style={{ fontSize: 13, color: 'var(--danger)', margin: '12px 0' }}>{preview.problem}</p>
        )}

        {result && (
          <div className="card" style={{ padding: 14, margin: '12px 0',
                                         borderLeft: '3px solid var(--ok, var(--accent))' }}>
            <div style={{ fontWeight: 650 }}>
              {result.raised} invoice{result.raised === 1 ? '' : 's'} raised · {rupees(result.totalPaise)}
            </div>
            {result.skipped > 0 && (
              <p style={{ fontSize: 12.5, color: 'var(--ink-2)', margin: '4px 0 0' }}>
                {result.skipped} skipped — they already had it.
              </p>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
          <button className="btn" onClick={onClose}>{result ? 'Close' : 'Cancel'}</button>
          {!result && (
            <>
              <button className="btn" disabled={busy !== '' || recipients === 0} onClick={() => void check()}>
                {busy === 'checking' ? 'Checking…' : 'Check'}
              </button>
              {/* ⚠ RAISE IS ONLY AVAILABLE AFTER A PREVIEW, and only when it found somebody to
                  charge. A one-click path would be a one-click way to bill four hundred families. */}
              <button className="btn btn-accent"
                disabled={busy !== '' || !preview || !!preview.problem || preview.newCount === 0}
                onClick={() => void raise()}>
                {busy === 'raising' ? 'Raising…'
                  : preview && !preview.problem ? `Raise ${preview.newCount}` : 'Raise'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ n, label, warn }: { n: string; label: string; warn?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 650, lineHeight: 1.1,
                    color: warn ? 'var(--warn)' : 'var(--ink)' }}>{n}</div>
      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{label}</div>
    </div>
  );
}
